/**
 * @fileoverview Slug profiles for generator → SaaS growth surfaces.
 * @description Maps professional-intent generators to funnel placements and
 * contextual copy. Companion cross-links stay separate (see
 * `docs/analytics/generator-growth-funnel.md`).
 */

/** Contextual marketing copy for a growth surface. */
export interface GeneratorGrowthCopy {
  /** Card / modal headline. */
  title: string
  /** Supporting body copy. */
  body: string
  /** Primary CTA label. */
  ctaLabel: string
  /** Short value bullets shown on the page upsell card. */
  bullets: readonly string[]
  /** Compact headline for the export-toolbar hint (narrow sidebar). */
  exportHintTitle: string
  /** One-line body for the export-toolbar hint. */
  exportHintBody: string
}

/** Optional deep-link surfaced after export or in-tool. */
export interface GeneratorVariantLink {
  href: string
  label: string
  description: string
}

/** Email client used by the email-thread generator. */
export type EmailThreadClient = 'gmail' | 'outlook'

/** Review platform used by the review mockup generator. */
export type ReviewMockupPlatform = 'google' | 'appstore' | 'yelp'

const COPY: Record<string, GeneratorGrowthCopy> = {
  'email-thread-mockup-generator': {
    title: 'Will the real ad perform?',
    body: 'You built the inbox proof — AdSights scores live creative variants before you ship campaigns.',
    ctaLabel: 'Analyze your ad creative',
    bullets: [
      'Predict hook and CTA performance before production',
      'Compare variants with ROAS tied to each creative',
      'Built for teams shipping social-proof mockups in decks',
    ],
    exportHintTitle: 'Score before you ship',
    exportHintBody: 'Know which creative variant will perform before the campaign goes live.',
  },
  'gmail-email-mockup-generator': {
    title: 'Gmail mockup done — what about performance?',
    body: 'AdSights tells you which inbox-style creative will earn clicks when you run paid.',
    ctaLabel: 'Request early access',
    bullets: [
      'Score email and social-proof creative together',
      'See which variant wins before the campaign ships',
    ],
    exportHintTitle: 'Inbox proof ready?',
    exportHintBody: 'Benchmark which email creative earns clicks before you run paid.',
  },
  'outlook-email-mockup-generator': {
    title: 'Outlook mockup done — what about performance?',
    body: 'AdSights benchmarks B2B creative so you know what converts before you publish.',
    ctaLabel: 'Request early access',
    bullets: [
      'Enterprise-ready creative analytics',
      'Variant testing tied back to pipeline metrics',
    ],
    exportHintTitle: 'Corporate mockup ready?',
    exportHintBody: 'See which B2B creative converts before you publish.',
  },
  'review-testimonial-generator': {
    title: 'Social proof is only half the story',
    body: 'Star ratings look great in mockups — AdSights shows which testimonial-led creative actually converts.',
    ctaLabel: 'Analyze your ad creative',
    bullets: [
      'Testimonial slides → measurable CTR and ROAS',
      'Compare hero vs proof-led layouts',
      'Pairs with your review and chat mockups',
    ],
    exportHintTitle: 'Social proof exported',
    exportHintBody: 'Find out which testimonial-led creative actually converts.',
  },
  'social-media-post-mockup-generator': {
    title: 'Previewing feeds is step one',
    body: '46% of visitors come back to refine mockups — AdSights tells you which post concept will convert before you publish.',
    ctaLabel: 'Request early access',
    bullets: [
      'Benchmark feed creative before ad spend',
      'Compare platform variants with performance data',
      'Drafts save locally — performance insight is the next step',
    ],
    exportHintTitle: 'Feed mockup ready?',
    exportHintBody: 'Benchmark which post concept converts before you publish.',
  },
  'discord-chat-generator': {
    title: 'Community proof exported — now score performance',
    body: 'Discord mockups win trust in decks — AdSights shows which creative variant will convert when you run paid.',
    ctaLabel: 'Request early access',
    bullets: [
      'Testimonial-style chat creative with measurable CTR',
      'Compare server/community layouts before campaigns ship',
    ],
    exportHintTitle: 'Discord mockup ready?',
    exportHintBody: 'See which community-proof creative converts before you publish.',
  },
  'group-chat-mockup-generator': {
    title: 'Group chat mockup done — what about ROAS?',
    body: 'Multi-party chat screenshots look authentic — AdSights benchmarks which group-thread concept earns clicks.',
    ctaLabel: 'Request early access',
    bullets: [
      'Score group-chat social proof before ad spend',
      'Compare thread layouts with performance data',
    ],
    exportHintTitle: 'Group chat exported?',
    exportHintBody: 'Benchmark which group-thread creative converts before you ship.',
  },
  'ad-mockup-generator': {
    title: 'Ad preview ready — will it perform?',
    body: 'Placement mockups help stakeholders align — AdSights scores live variants before production spend.',
    ctaLabel: 'Analyze your ad creative',
    bullets: [
      'Predict hook and CTA performance across placements',
      'Compare variants with ROAS tied to each creative',
    ],
    exportHintTitle: 'Placement mockup ready?',
    exportHintBody: 'Know which ad variant will perform before the campaign goes live.',
  },
  'data-table-generator': {
    title: 'Reporting mockup exported',
    body: 'Dashboard screenshots sell the story — AdSights ties each creative variant back to measurable performance.',
    ctaLabel: 'Request early access',
    bullets: [
      'Turn reporting visuals into testable ad concepts',
      'Compare data-led layouts before you publish',
    ],
    exportHintTitle: 'Table mockup ready?',
    exportHintBody: 'Benchmark which data-led creative converts before you ship.',
  },
  'slack-message-generator': {
    title: 'Slack proof exported — score before you ship',
    body: 'Workplace chat mockups build B2B credibility — AdSights benchmarks which thread concept converts.',
    ctaLabel: 'Request early access',
    bullets: [
      'Enterprise social-proof creative with measurable CTR',
      'Compare channel layouts before campaigns go live',
    ],
    exportHintTitle: 'Slack mockup ready?',
    exportHintBody: 'See which workplace-proof creative converts before you publish.',
  },
  'whatsapp-chat-generator': {
    title: 'WhatsApp mockup exported — will it convert?',
    body: 'Mobile chat screenshots drive social proof — AdSights scores which thread concept earns clicks.',
    ctaLabel: 'Request early access',
    bullets: [
      'Benchmark mobile chat creative before ad spend',
      'Compare thread layouts with performance data',
    ],
    exportHintTitle: 'WhatsApp mockup ready?',
    exportHintBody: 'See which mobile chat creative converts before you publish.',
  },
  'imessage-chat-generator': {
    title: 'iMessage mockup exported — will it convert?',
    body: 'Phone-native chat screenshots are high-trust social proof — AdSights scores which thread concept earns clicks.',
    ctaLabel: 'Request early access',
    bullets: [
      'Benchmark mobile chat creative before ad spend',
      'Compare thread layouts with performance data',
    ],
    exportHintTitle: 'iMessage mockup ready?',
    exportHintBody: 'See which mobile chat creative converts before you publish.',
  },
  'messenger-chat-generator': {
    title: 'Messenger mockup exported — will it convert?',
    body: 'Messenger-style social proof reads as personal — AdSights benchmarks which chat concept converts when you run paid.',
    ctaLabel: 'Request early access',
    bullets: [
      'Score chat social proof before ad spend',
      'Compare thread layouts with performance data',
    ],
    exportHintTitle: 'Messenger mockup ready?',
    exportHintBody: 'See which chat creative converts before you publish.',
  },
}

const DEFAULT_COPY: GeneratorGrowthCopy = {
  title: 'Stop guessing creative performance',
  body: 'AdSights tracks every variant and ties results back to ROAS automatically.',
  ctaLabel: 'Request early access',
  bullets: ['AI creative scoring', 'Variant comparison', 'ROAS attribution'],
  exportHintTitle: 'Score before you ship',
  exportHintBody: 'AdSights tracks variant performance and ties results to ROAS.',
}

/** Platform-qualified social mockup landing variants (GSC clusters). */
const SOCIAL_MOCKUP_VARIANT_SLUGS = [
  'instagram-post-mockup-generator',
  'linkedin-post-mockup-generator',
  'x-post-mockup-generator',
  'facebook-post-mockup-generator',
  'tiktok-post-mockup-generator',
  'youtube-mockup-generator',
  'instagram-reels-mockup-generator',
] as const

/** Platform-qualified review mockup landing variants. */
const REVIEW_MOCKUP_VARIANT_SLUGS = [
  'google-business-review-generator',
  'app-store-review-generator',
  'yelp-review-generator',
] as const

/**
 * Maps variant slugs to umbrella copy keys. Variant pages pass their own
 * `trackingSlug` for analytics but reuse umbrella messaging.
 */
const GROWTH_COPY_ALIASES: Record<string, string> = {
  ...Object.fromEntries(SOCIAL_MOCKUP_VARIANT_SLUGS.map(slug => [slug, 'social-media-post-mockup-generator'])),
  ...Object.fromEntries(REVIEW_MOCKUP_VARIANT_SLUGS.map(slug => [slug, 'review-testimonial-generator'])),
}

/** Generators that show the post-export `/request-access` modal after PNG export. */
export const GENERATOR_POST_EXPORT_FUNNEL_SLUGS = new Set([
  'email-thread-mockup-generator',
  'gmail-email-mockup-generator',
  'outlook-email-mockup-generator',
  'review-testimonial-generator',
  ...REVIEW_MOCKUP_VARIANT_SLUGS,
  'social-media-post-mockup-generator',
  ...SOCIAL_MOCKUP_VARIANT_SLUGS,
])

/** Generators that show the page upsell card below intro + companion links. */
export const GENERATOR_PAGE_UPSELL_SLUGS = new Set([
  'email-thread-mockup-generator',
  'gmail-email-mockup-generator',
  'outlook-email-mockup-generator',
  'review-testimonial-generator',
  ...REVIEW_MOCKUP_VARIANT_SLUGS,
  'social-media-post-mockup-generator',
  ...SOCIAL_MOCKUP_VARIANT_SLUGS,
  // High-engagement June 2026 cohort — page upsell only (export modal not wired yet).
  'discord-chat-generator',
  'group-chat-mockup-generator',
  'ad-mockup-generator',
  'data-table-generator',
  'slack-message-generator',
  'whatsapp-chat-generator',
  // July 2026: the remaining chat variants join the page upsell so every
  // platform-qualified chat landing page carries the /request-access
  // callout. The fake-chat UMBRELLA remains intentionally excluded — see
  // docs/analytics/generator-growth-funnel.md ("Intentionally excluded":
  // prank-adjacent traffic dilutes measured CTR, R3 gate).
  'imessage-chat-generator',
  'messenger-chat-generator',
])

/** Generators that show the compact export-toolbar bridge near Download. */
export const GENERATOR_EXPORT_HINT_SLUGS = GENERATOR_POST_EXPORT_FUNNEL_SLUGS

/**
 * Returns contextual copy for a generator slug.
 *
 * @param {string} slug - Generator slug.
 * @returns {GeneratorGrowthCopy} Headline, body, CTA, and bullets.
 */
export function getGeneratorGrowthCopy(slug: string): GeneratorGrowthCopy {
  const copyKey = GROWTH_COPY_ALIASES[slug] ?? slug
  return COPY[copyKey] ?? DEFAULT_COPY
}

/**
 * Returns whether a generator slug should render the page upsell card.
 *
 * @param {string} slug - Generator slug.
 * @returns {boolean} True when the upsell card should render.
 */
export function generatorHasPageUpsell(slug: string): boolean {
  return GENERATOR_PAGE_UPSELL_SLUGS.has(slug)
}

/**
 * Returns whether a generator slug should show the post-export prompt.
 *
 * @param {string} slug - Generator slug.
 * @returns {boolean} True when post-export funnel is enabled.
 */
export function generatorHasPostExportFunnel(slug: string): boolean {
  return GENERATOR_POST_EXPORT_FUNNEL_SLUGS.has(slug)
}

/**
 * Returns whether a generator slug should show the export-toolbar hint.
 *
 * @param {string} slug - Generator slug.
 * @returns {boolean} True when the inline export hint should render.
 */
export function generatorHasExportHint(slug: string): boolean {
  return GENERATOR_EXPORT_HINT_SLUGS.has(slug)
}

/**
 * Variant deep-links for the email-thread post-export modal.
 *
 * @param {EmailThreadClient} client - Active email client.
 * @param {string} trackingSlug - Current generator slug.
 * @returns {PostExportVariantLink[]} Sibling variant links.
 */
export function resolveEmailPostExportVariants(
  client: EmailThreadClient,
  trackingSlug: string,
): readonly GeneratorVariantLink[] {
  if (trackingSlug !== 'email-thread-mockup-generator') return []
  if (client === 'gmail') {
    return [
      {
        href: '/resources/tools/generators/outlook-email-mockup-generator',
        label: 'Need Outlook chrome instead?',
        description: 'Desktop ribbon, folder pane, and corporate threading UI.',
      },
    ]
  }
  return [
    {
      href: '/resources/tools/generators/gmail-email-mockup-generator',
      label: 'Need Gmail chrome instead?',
      description: 'Inbox tabs, stars, and consumer-friendly web layout.',
    },
  ]
}

/**
 * Variant deep-links for the review post-export modal.
 *
 * @param {ReviewMockupPlatform} platform - Active review platform.
 * @param {string} trackingSlug - Current generator slug.
 * @returns {PostExportVariantLink[]} Sibling variant links.
 */
export function resolveReviewPostExportVariants(
  platform: ReviewMockupPlatform,
  trackingSlug: string,
): readonly GeneratorVariantLink[] {
  if (trackingSlug !== 'review-testimonial-generator') return []

  const variants: Record<ReviewMockupPlatform, GeneratorVariantLink> = {
    google: {
      href: '/resources/tools/generators/app-store-review-generator',
      label: 'Need App Store reviews instead?',
      description: 'iOS star ratings and “Verified purchase” styling.',
    },
    appstore: {
      href: '/resources/tools/generators/google-business-review-generator',
      label: 'Need Google Business reviews instead?',
      description: 'Local search stars and owner-response layout.',
    },
    yelp: {
      href: '/resources/tools/generators/google-business-review-generator',
      label: 'Need Google Business reviews instead?',
      description: 'Switch to local-search star-rating chrome.',
    },
  }

  const link = variants[platform]
  return link ? [link] : []
}
